import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { Archive, ArrowDownUp, ArrowLeft, Filter, RotateCcw, Search as SearchIcon, Trash2, X } from 'lucide-react';
import { Badge, Button, EmptyState, Input, Menu, MenuItem, MenuDivider, Popover, useToast, cn } from '@vd/ui';
import type { ProjectViewPrefs, TicketSummary, User } from '@vd/types';
import { api } from '../lib/api';
import { useAuth } from '../features/auth/AuthProvider';
import { CardsView } from '../features/board/CardsView';
import { TableView } from '../features/board/TableView';
import { TicketListLoading } from '../features/board/TicketListLoading';
import { ViewToggle } from '../features/board/ViewToggle';
import { FilterModal, type FilterState } from '../features/modals/FilterModal';
import { BranchNameModal } from '../features/ticket/BranchNameModal';
import { copyToClipboard } from '../lib/clipboard';
import { safeOpen } from '../lib/safeOpen';
import { ARCHIVE_SCREEN, EVERY_SCREEN } from '../lib/screenNames';
import { useTabProject, useTabTitle, useTabWorkspaceName } from '../lib/tabTitle';
import { wallTimeEndIn, wallTimeIn } from '../lib/time';

/**
 * Archived-tickets view. Same Cards / Table view options the board has,
 * with an Archive-appropriate toolbar (sort, filter, search, bulk
 * select). Restore + Delete live in the per-card overflow menu so the
 * Cards/Table renderers stay identical to the board's; the destructive
 * confirm + bulk-delete bar replaces the kanban's "create ticket" bar.
 */

type ArchiveView = 'cards' | 'table';

type SortKey = 'newest' | 'oldest' | 'last-activity' | 'most-comments';

const SORT_OPTIONS: Array<{ key: SortKey; label: string; short: string }> = [
  { key: 'newest',        label: 'Newest first',  short: 'Newest' },
  { key: 'oldest',        label: 'Oldest first',  short: 'Oldest' },
  { key: 'last-activity', label: 'Last activity', short: 'Activity' },
  { key: 'most-comments', label: 'Most comments', short: 'Comments' },
];

const EMPTY_FILTER: FilterState = {
  quickFilters: {
    hasAiActivity: false,
    capturePartial: false,
    unresolved: false,
    mentionsMe: false,
    archived: false,
  },
  assigneeIds: [],
  authorIds: [],
  columnIds: [],
  createdFrom: '',
  createdTo: '',
};

/**
 * The line that says some of these will be deleted (TOADO-442), or nothing when none
 * will. A ticket removed from a page with the extension is archived and deleted ten
 * days later unless it is restored; the extension's notice links here, so this is
 * where somebody arrives to restore one.
 */
export function removedTicketsNote(removedCount: number): string | null {
  if (removedCount <= 0) return null;
  const which = removedCount === 1 ? 'One of these was' : `${removedCount} of these were`;
  return `${which} removed from a page with the Toado extension. Tickets removed that way are deleted 10 days after they were removed, unless you restore them.`;
}

export function ArchivePage() {
  // This tab's title and icon: the project's mark (TOADO-905).
  const tabWorkspace = useTabWorkspaceName();
  const tabProject = useTabProject(useParams<{ projectId: string }>().projectId);
  useTabTitle({ parts: [tabProject?.name, "Archive", tabWorkspace], project: tabProject });
  const { projectId } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const { user, currentCompanyId } = useAuth();

  const projectQ = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => api.getProject(projectId!),
    enabled: !!projectId,
  });

  const ticketsQ = useQuery({
    queryKey: ['tickets', projectId, 'archived'],
    queryFn: () => api.listTickets(projectId!, 'archived'),
    enabled: !!projectId,
  });

  const columnsQ = useQuery({
    queryKey: ['columns', projectId],
    queryFn: () => api.listColumns(projectId!),
    enabled: !!projectId,
  });

  const membersQ = useQuery({
    queryKey: ['members-all'],
    queryFn: () => currentCompanyId ? api.listMembers(currentCompanyId) : Promise.resolve([]),
    enabled: !!currentCompanyId,
  });

  // Per-card "in-flight" indicator. Same pattern as the project board:
  // every card-mutating action marks its ticket id(s) so CardsView /
  // TableView render the existing dim+spinner overlay on the card via
  // KCard's `isMoving` prop. Cleared on settle.
  const [movingTicketIds, setMovingTicketIds] = useState<Set<string>>(new Set());
  const markPending = (ids: string | string[]) => {
    const arr = Array.isArray(ids) ? ids : [ids];
    setMovingTicketIds((prev) => {
      const n = new Set(prev);
      for (const id of arr) n.add(id);
      return n;
    });
  };
  const unmarkPending = (ids: string | string[]) => {
    const arr = Array.isArray(ids) ? ids : [ids];
    setMovingTicketIds((prev) => {
      const n = new Set(prev);
      for (const id of arr) n.delete(id);
      return n;
    });
  };

  const restoreMutation = useMutation({
    mutationFn: (ticketId: string) => api.restoreTicket(ticketId),
    onMutate: (ticketId) => { markPending(ticketId); },
    onSettled: (_data, _err, ticketId) => { unmarkPending(ticketId); },
    onSuccess: (_res, ticketId) => {
      qc.invalidateQueries({ queryKey: ['tickets', projectId, 'archived'] });
      qc.invalidateQueries({ queryKey: ['tickets', projectId] });
      toast.push({ variant: 'success', title: 'Ticket restored', description: `${ticketId} is back on the board.` });
    },
    onError: (err) => {
      toast.push({ variant: 'danger', title: 'Restore failed', description: (err as Error).message });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (ticketId: string) => api.deleteTicket(ticketId),
    onMutate: (ticketId) => { markPending(ticketId); },
    onSettled: (_data, _err, ticketId) => { unmarkPending(ticketId); },
  });

  const allTickets = ticketsQ.data?.items ?? [];
  const removedNote = removedTicketsNote(allTickets.filter((ticket) => ticket.purgeAt).length);
  const columns = columnsQ.data ?? [];
  const membersById = useMemo(() => {
    const m = new Map<string, User>();
    membersQ.data?.forEach(({ user: u }) => m.set(u.id, u));
    return m;
  }, [membersQ.data]);
  // Cards/Table want a creator map separately so the byline can render
  // even for users no longer on the team. Same map suffices since
  // archived tickets keep their createdBy reference.
  const creatorsById = membersById;

  // ---- Toolbar state ---------------------------------------------------
  const [viewMode, setViewMode] = useState<ArchiveView>('cards');
  const [sort, setSort] = useState<SortKey>('newest');
  const [filter, setFilter] = useState<FilterState>(EMPTY_FILTER);
  const [filterOpen, setFilterOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [sortAnchor, setSortAnchor] = useState<DOMRect | null>(null);

  const filteredTickets = useMemo(() => {
    let result = allTickets.slice();

    if (filter.assigneeIds.length) {
      result = result.filter((t) => filter.assigneeIds.some((id) => t.assignees.includes(id)));
    }
    if (filter.authorIds.length) {
      result = result.filter((t) => filter.authorIds.includes(t.createdBy));
    }
    if (filter.columnIds.length) {
      result = result.filter((t) => filter.columnIds.includes(t.columnId));
    }
    if (filter.quickFilters.hasAiActivity) {
      result = result.filter((t) => t.hasAiActivity);
    }
    if (filter.quickFilters.capturePartial) {
      result = result.filter((t) => t.health === 'partial');
    }
    if (filter.quickFilters.unresolved) {
      result = result.filter((t) => !t.resolved);
    }
    if (filter.quickFilters.mentionsMe && user) {
      result = result.filter((t) => t.mentions?.includes(user.id));
    }
    if (filter.createdFrom) {
      const fromMs = wallTimeIn(filter.createdFrom)?.getTime() ?? NaN;
      if (Number.isFinite(fromMs)) result = result.filter((t) => new Date(t.createdAt).getTime() >= fromMs);
    }
    if (filter.createdTo) {
      const toMs = wallTimeEndIn(filter.createdTo)?.getTime() ?? NaN;
      if (Number.isFinite(toMs)) result = result.filter((t) => new Date(t.createdAt).getTime() <= toMs);
    }
    // Note: filter.quickFilters.archived is intentionally ignored —
    // every ticket on this page is archived, so the chip is a no-op
    // here. Hiding it from the modal would require a config prop on
    // FilterModal; not worth the surface yet.

    if (query.trim()) {
      const q = query.toLowerCase();
      result = result.filter((t) =>
        t.title.toLowerCase().includes(q) ||
        (t.descriptionPreview ?? '').toLowerCase().includes(q),
      );
    }

    const sorted = result.slice();
    if (sort === 'newest') sorted.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    if (sort === 'oldest') sorted.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    if (sort === 'last-activity') sorted.sort((a, b) => (b.updatedAt ?? '').localeCompare(a.updatedAt ?? ''));
    if (sort === 'most-comments') sorted.sort((a, b) => b.commentCount - a.commentCount);
    return sorted;
  }, [allTickets, filter, sort, query, user]);

  // Active-filter count used to badge the Filter button.
  const activeFilterCount =
    Object.values(filter.quickFilters).filter((v) => v && v !== filter.quickFilters.archived).length +
    filter.assigneeIds.length +
    filter.authorIds.length +
    filter.columnIds.length +
    (filter.createdFrom ? 1 : 0) +
    (filter.createdTo ? 1 : 0);

  // ---- Selection state (multi-select / bulk delete) --------------------
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const visibleIds = useMemo(() => filteredTickets.map((t) => t.id), [filteredTickets]);
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
  const anySelected = selected.size > 0;

  const toggleOne = (id: string, next: boolean) => {
    setSelected((prev) => {
      const out = new Set(prev);
      if (next) out.add(id); else out.delete(id);
      return out;
    });
  };
  const toggleAll = () => {
    setSelected((prev) => prev.size === visibleIds.length ? new Set() : new Set(visibleIds));
  };
  const clearSelection = () => setSelected(new Set());

  // ---- Overflow menu state --------------------------------------------
  const [overflow, setOverflow] = useState<{ t: TicketSummary; anchor: DOMRect } | null>(null);
  const [branchTarget, setBranchTarget] = useState<TicketSummary | null>(null);
  const handleOverflowClick = (t: TicketSummary, anchorEl: HTMLElement) => {
    setOverflow({ t, anchor: anchorEl.getBoundingClientRect() });
  };

  const [confirmTargets, setConfirmTargets] = useState<TicketSummary[] | null>(null);

  const handleOpen = (t: TicketSummary) => navigate(`/tickets/${t.id}`);
  const handleCopyLink = async (t: TicketSummary) => {
    await copyToClipboard(`${window.location.origin}/tickets/${t.id}`);
    toast.push({ variant: 'success', title: 'Link copied' });
  };
  const handleOpenSource = (t: TicketSummary) => safeOpen(t.sourceUrl);

  const runDelete = async (targets: TicketSummary[]) => {
    // deleteMutation's own onMutate/onSettled mark + unmark per-ticket;
    // no need to wrap with markPending here.
    const results = await Promise.allSettled(targets.map((t) => deleteMutation.mutateAsync(t.id)));
    const ok = results.filter((r) => r.status === 'fulfilled').length;
    const failed = results.length - ok;
    qc.invalidateQueries({ queryKey: ['tickets', projectId, 'archived'] });
    qc.invalidateQueries({ queryKey: ['tickets', projectId] });
    setConfirmTargets(null);
    setSelected(new Set());
    if (failed === 0) {
      toast.push({
        variant: 'success',
        title: ok === 1 ? 'Ticket deleted' : `${ok} tickets deleted`,
      });
    } else {
      toast.push({
        variant: 'danger',
        title: `Deleted ${ok}, failed ${failed}`,
        description: 'Some deletes did not complete. Refresh and try the failed ones again.',
      });
    }
  };

  // Bulk restore. Bypasses `restoreMutation` (which fires a per-ticket
  // toast intended for the single-restore path from the overflow menu)
  // and emits one summary toast at the end. Restoration is non-
  // destructive, so no confirm dialog — same shape as bulk-archive on
  // the board.
  const [bulkRestoring, setBulkRestoring] = useState(false);
  const runBulkRestore = async (targets: TicketSummary[]) => {
    const targetIds = targets.map((t) => t.id);
    setBulkRestoring(true);
    markPending(targetIds);
    try {
      const results = await Promise.allSettled(targets.map((t) => api.restoreTicket(t.id)));
      const ok = results.filter((r) => r.status === 'fulfilled').length;
      const failed = results.length - ok;
      qc.invalidateQueries({ queryKey: ['tickets', projectId, 'archived'] });
      qc.invalidateQueries({ queryKey: ['tickets', projectId] });
      setSelected(new Set());
      if (failed === 0) {
        toast.push({
          variant: 'success',
          title: ok === 1 ? 'Ticket restored' : `${ok} tickets restored`,
        });
      } else {
        toast.push({
          variant: 'danger',
          title: `Restored ${ok}, failed ${failed}`,
          description: 'Some restores did not complete. Refresh and try the failed ones again.',
        });
      }
    } finally {
      setBulkRestoring(false);
      unmarkPending(targetIds);
    }
  };

  const sortLabel = SORT_OPTIONS.find((s) => s.key === sort)?.short ?? 'Sort';

  return (
    // Screens, for pins (TOADO-701): the cards and table views are each one,
    // at this one address, and the rest of the page is on both.
    <div style={{ padding: '24px 40px', overflowY: 'auto', width: '100%' }} data-toado-view={EVERY_SCREEN}>
      <button
        onClick={() => navigate(`/projects/${projectId}/board`)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '4px 8px', marginBottom: 16,
          fontSize: 13, color: 'var(--text-tertiary)',
          background: 'transparent', border: 'none', borderRadius: 6, cursor: 'pointer',
        }}
      >
        <ArrowLeft size={12} />
        Back to {projectQ.data?.name ?? 'project'}
      </button>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <h1 style={{ fontSize: 24, margin: 0 }}>Archived tickets</h1>
        <span style={{
          padding: '2px 10px', borderRadius: 999,
          background: 'var(--bg-muted)', border: '1px solid var(--border)',
          fontSize: 12, color: 'var(--text-secondary)',
          fontFamily: '"JetBrains Mono", monospace',
        }}>
          {filteredTickets.length}{filteredTickets.length !== allTickets.length ? ` of ${allTickets.length}` : ''}
        </span>
      </div>
      {removedNote && (
        <p style={{ margin: '0 0 16px', fontSize: 13, color: 'var(--text-secondary)' }}>{removedNote}</p>
      )}

      {/* Toolbar: search + view toggle + sort + filter + bulk bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: '0 0 240px', maxWidth: 320 }}>
          <SearchIcon
            size={14}
            style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)', pointerEvents: 'none' }}
          />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search archive…"
            style={{ paddingLeft: 30 }}
          />
        </div>

        <ViewToggle viewMode={viewMode} onChange={(v) => setViewMode(v as ArchiveView)} modes={['cards', 'table']} />

        <Button
          variant="ghost"
          size="sm"
          leftIcon={<ArrowDownUp size={12} />}
          onClick={(e) => setSortAnchor((e.currentTarget as HTMLElement).getBoundingClientRect())}
        >
          {sortLabel}
        </Button>
        <Popover open={!!sortAnchor} onClose={() => setSortAnchor(null)} anchor={sortAnchor} placement="bottom-start">
          <Menu>
            {SORT_OPTIONS.map((opt) => (
              <MenuItem key={opt.key} onClick={() => { setSort(opt.key); setSortAnchor(null); }}>
                {opt.label}
              </MenuItem>
            ))}
          </Menu>
        </Popover>

        <Button
          variant="ghost"
          size="sm"
          leftIcon={<Filter size={12} />}
          onClick={() => setFilterOpen(true)}
        >
          Filter
          {activeFilterCount > 0 && (
            <Badge variant="solid" style={{ marginLeft: 6 }}>{activeFilterCount}</Badge>
          )}
        </Button>

        {filteredTickets.length > 0 && (
          <label style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            fontSize: 13, color: 'var(--text-secondary)', cursor: 'pointer',
            marginLeft: 8,
          }}>
            <input
              type="checkbox"
              checked={allSelected}
              ref={(el) => { if (el) el.indeterminate = anySelected && !allSelected; }}
              onChange={toggleAll}
            />
            Select all
          </label>
        )}

        {anySelected && (
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
              {selected.size} selected
            </span>
            <Button variant="ghost" size="sm" leftIcon={<X size={12} />} onClick={clearSelection}>
              Clear
            </Button>
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<RotateCcw size={12} />}
              onClick={() => runBulkRestore(filteredTickets.filter((t) => selected.has(t.id)))}
              disabled={bulkRestoring}
            >
              Restore {selected.size}
            </Button>
            <Button
              variant="destructive"
              size="sm"
              leftIcon={<Trash2 size={12} />}
              onClick={() => setConfirmTargets(filteredTickets.filter((t) => selected.has(t.id)))}
              disabled={deleteMutation.isPending}
            >
              Delete {selected.size}
            </Button>
          </div>
        )}
      </div>

      {ticketsQ.isLoading ? (
        <TicketListLoading />
      ) : filteredTickets.length === 0 ? (
        <EmptyState
          icon={<Archive size={28} />}
          title={allTickets.length === 0 ? 'Nothing archived yet' : 'No tickets match these filters'}
          description={allTickets.length === 0
            ? 'Tickets archived manually or by policy appear here. Restore them from this page or from the ticket detail menu.'
            : 'Try clearing search or filters.'}
        />
      ) : viewMode === 'cards' ? (
        <CardsView
          screen={`${ARCHIVE_SCREEN}/cards`}
          tickets={filteredTickets}
          columns={columns}
          creatorsById={creatorsById}
          membersById={membersById}
          showThumbnails
          onOpenTicket={handleOpen}
          onCopyLink={handleCopyLink}
          onOpenSource={handleOpenSource}
          onOverflowClick={handleOverflowClick}
          bulkSelection={selected}
          onBulkToggle={toggleOne}
          movingTicketIds={movingTicketIds}
        />
      ) : (
        <TableView
          screen={`${ARCHIVE_SCREEN}/table`}
          tickets={filteredTickets}
          columns={columns}
          creatorsById={creatorsById}
          membersById={membersById}
          onOpenTicket={handleOpen}
          onCopyLink={handleCopyLink}
          onOpenSource={handleOpenSource}
          onOverflowClick={handleOverflowClick}
          bulkSelection={selected}
          onBulkToggle={toggleOne}
          movingTicketIds={movingTicketIds}
        />
      )}

      <Popover
        open={!!overflow}
        onClose={() => setOverflow(null)}
        anchor={overflow?.anchor ?? null}
        placement="bottom-end"
      >
        <Menu>
          <MenuItem onClick={() => { if (overflow) handleOpen(overflow.t); setOverflow(null); }}>
            Open ticket
          </MenuItem>
          <MenuItem onClick={() => { if (overflow) handleCopyLink(overflow.t); setOverflow(null); }}>
            Copy link
          </MenuItem>
          {overflow?.t.branchName && (
            <MenuItem
              onClick={async () => {
                if (!overflow?.t.branchName) return;
                await copyToClipboard(overflow.t.branchName);
                toast.push({ variant: 'success', title: 'Branch name copied' });
                setOverflow(null);
              }}
            >
              {`Branch: ${overflow.t.branchName}`}
            </MenuItem>
          )}
          <MenuItem
            onClick={() => {
              if (!overflow) return;
              setBranchTarget(overflow.t);
              setOverflow(null);
            }}
          >
            {overflow?.t.branchName ? 'Edit branch name…' : 'Set branch name…'}
          </MenuItem>
          {overflow?.t.sourceUrl && (
            <MenuItem onClick={() => { if (overflow) handleOpenSource(overflow.t); setOverflow(null); }}>
              Open source URL
            </MenuItem>
          )}
          <MenuDivider />
          <MenuItem
            onClick={() => {
              if (!overflow) return;
              restoreMutation.mutate(overflow.t.id);
              setOverflow(null);
            }}
          >
            Restore
          </MenuItem>
          <MenuItem
            destructive
            onClick={() => {
              if (!overflow) return;
              setConfirmTargets([overflow.t]);
              setOverflow(null);
            }}
          >
            Delete…
          </MenuItem>
        </Menu>
      </Popover>

      {branchTarget && (
        <BranchNameModal
          open={!!branchTarget}
          onClose={() => setBranchTarget(null)}
          ticketId={branchTarget.id}
          projectId={branchTarget.projectId}
          currentBranch={branchTarget.branchName}
        />
      )}

      <FilterModal
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        value={filter}
        onApply={(next) => { setFilter(next); setFilterOpen(false); }}
        columns={columns}
      />

      {confirmTargets && (
        <div
          onClick={() => setConfirmTargets(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 100,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 8,
              padding: 20,
              maxWidth: 480,
              width: '90%',
            }}
          >
            <h2 style={{ margin: '0 0 8px', fontSize: 18 }}>
              Delete {confirmTargets.length === 1 ? 'this ticket' : `${confirmTargets.length} tickets`}?
            </h2>
            <p style={{ margin: '0 0 16px', fontSize: 13, color: 'var(--text-secondary)' }}>
              This permanently removes
              {confirmTargets.length === 1 ? ' the ticket ' : ` ${confirmTargets.length} tickets `}
              along with their captures, comments, and attachments. This can't be undone.
            </p>
            {confirmTargets.length <= 5 && (
              <ul style={{ margin: '0 0 16px', paddingLeft: 18, fontSize: 13, color: 'var(--text-secondary)' }}>
                {confirmTargets.map((t) => <li key={t.id}>{t.title}</li>)}
              </ul>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button variant="ghost" size="sm" onClick={() => setConfirmTargets(null)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => runDelete(confirmTargets)}
                loading={deleteMutation.isPending}
              >
                Delete {confirmTargets.length === 1 ? '' : confirmTargets.length}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
